// ============================================
// FILE: components/quiz/QuestionView.tsx
// ============================================
import React, { useState } from 'react';
import { TextView } from './TextView';
import { MushafView } from './MushafView';
import DataService from '../../services/DataService';

interface QuestionViewProps {
  questionNumber: number;
  question: any;
  onNext: () => void;
  onDone: () => void;
  onPrev?: () => void;
  showPrev?: boolean;
  readingMode?: string; // 'mushaf' or 'hifz' or 'memory'
}

export const QuestionView: React.FC<QuestionViewProps> = ({ 
  questionNumber, 
  question,
  onNext, 
  onDone,
  onPrev,
  showPrev,
  readingMode = 'mushaf' // default to mushaf
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [viewMode, setViewMode] = useState<'question' | 'mushaf' | 'text'>('question');

  // Determine if we're in memory/hifz mode
  const isMemoryMode = readingMode.toLowerCase() === 'hifz' || readingMode.toLowerCase() === 'memory';
  const isMushafMode = readingMode.toLowerCase() === 'mushaf';

  if (!question) {
    return (
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl mx-auto p-6 text-center">
        <h2 className="text-2xl font-bold mb-4">Question Not Found</h2>
        <p className="text-gray-600 mb-4">This question is not available.</p>
        <button
          onClick={onDone}
          className="bg-gradient-to-r from-purple-500 to-purple-700 text-white px-6 py-2 rounded-lg"
        >
          Back to Grid
        </button>
      </div>
    );
  }

  const handleShowAnswer = () => {
    setShowAnswer(true);
  };

  const handleOptionSelect = (option: string) => {
    setSelectedAnswer(option);
  };

  const switchToQuestion = () => setViewMode('question');
  const switchToMushaf = () => setViewMode('mushaf');
  const switchToText = () => setViewMode('text');

  // Get the verse ID from the question
  const verseId = question.vid || question.verse_id || question.id;
  const pageNumber = question.page || 'N/A';

  // Get the current view label
  const getCurrentViewLabel = () => {
    if (viewMode === 'question') return 'Question View';
    if (viewMode === 'mushaf') return '🖼️ Mushaf Page View';
    return '📄 Text View';
  };

  // Check if we're in fullscreen mode (mushaf or text view)
  const isFullscreenView = viewMode === 'mushaf' || viewMode === 'text';

  return (
    <div className={`bg-white rounded-xl shadow-2xl mx-auto ${isFullscreenView ? 'w-full max-w-full' : 'max-w-4xl'} ${isFullscreenView ? 'p-0' : 'p-6'}`}>
      {/* Header - Always visible */}
      <div className={`flex flex-wrap justify-between items-center gap-2 ${isFullscreenView ? 'px-4 py-3 bg-purple-50 rounded-t-xl' : 'mb-6'}`}>
        <h2 className="text-2xl font-bold">
          Question {questionNumber}
        </h2>
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-sm text-gray-500">
            Surah {question.sura_number} • Verse {question.aya_number}
          </span>
          <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
            {isMemoryMode ? '🧠 Memory' : '📖 Mushaf'}
          </span>
          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
            {getCurrentViewLabel()}
          </span>
        </div>
      </div>

      {/* Memory Mode - Only show question with details */}
      {isMemoryMode ? (
        <div className="p-6">
          {/* Question View for Memory Mode */}
          <div className="mb-6 p-4 bg-purple-50 rounded-lg">
            <p className="text-lg text-right font-arabic leading-loose">
              {question.verse_text || question.first_verse}
            </p>
            <div className="flex flex-wrap justify-between items-center mt-2 gap-2">
              <div className="flex flex-wrap gap-3">
                <p className="text-sm text-gray-600">
                  📖 {question.surah_name_ar} - Verse {question.aya_number}
                </p>
                <p className="text-xs text-gray-400">
                  Page {pageNumber} • Juz {question.juz}
                </p>
              </div>
            </div>
          </div>

          {/* Answer Options
          <div className="space-y-2 mb-6">
            <p className="font-semibold text-gray-700">Select your answer:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {['Option A', 'Option B', 'Option C', 'Option D'].map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleOptionSelect(option)}
                  className={`p-3 border rounded-lg text-left transition-all ${
                    selectedAnswer === option 
                      ? 'border-purple-600 bg-purple-50 shadow-md' 
                      : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                  }`}
                >
                  <span className="font-medium">{String.fromCharCode(65 + index)}.</span> {option}
                </button>
              ))}
            </div>
          </div> */}

          {/* {showAnswer && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="font-semibold text-green-800">✅ Correct Answer:</p>
              <p className="text-green-700 mt-1">
                The correct answer is Option A
              </p>
              <div className="mt-2 text-sm text-gray-600">
                <p><strong>Reference:</strong> Surah {question.surah_name_ar} ({question.sura_number}:{question.aya_number})</p>
                <p><strong>Page:</strong> {pageNumber}</p>
              </div>
            </div>
          )} */}

          <div className="flex flex-wrap justify-between items-center gap-2">
            <div className="flex flex-wrap gap-2">
              {showPrev && onPrev && (
                <button
                  onClick={onPrev}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                >
                  ← Previous
                </button>
              )}
              <button
                onClick={handleShowAnswer}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                disabled={showAnswer}
              >
                {showAnswer ? '✅ Answer Revealed' : 'Show Answer'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={onDone}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Grid
              </button>
              <button
                onClick={onNext}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-700 text-white rounded-lg hover:scale-105 transition-transform"
              >
                {questionNumber === 2 ? '✅ Done' : 'Next →'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Mushaf Mode - Show Question, Mushaf View, and Text View
        <>
          {/* View Toggle Buttons - Only for Mushaf Mode */}
          <div className={`flex flex-wrap gap-2 ${isFullscreenView ? 'px-4 py-2 bg-gray-50' : 'mb-4'}`}>
            <button
              onClick={switchToQuestion}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                viewMode === 'question'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              📝 Question
            </button>
            <button
              onClick={switchToMushaf}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                viewMode === 'mushaf'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              🖼️ Mushaf View
            </button>
            <button
              onClick={switchToText}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                viewMode === 'text'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              📄 Text View
            </button>
          </div>

          {/* Content based on view mode - Mushaf Mode */}
          {viewMode === 'question' ? (
            // Question View - with padding
            <div className="p-6">
              <div className="mb-6 p-4 bg-purple-50 rounded-lg ">
                <p className="text-lg text-right ayah leading-loose">
                  {question.verse_text || question.first_verse}
                </p>
                <div className="flex flex-wrap justify-between items-center mt-2 gap-2">
                  <div className="flex flex-wrap gap-3">
                    <p className="text-sm text-gray-600">
                      📖 {question.surah_name_ar} - Verse {question.aya_number}
                    </p>
                    <p className="text-xs text-gray-400">
                      Page {pageNumber} • Juz {question.juz}
                    </p>
                  </div>
                </div>
              </div>

              {/* Answer Options */}
              {/* <div className="space-y-2 mb-6">
                <p className="font-semibold text-gray-700">Select your answer:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {['Option A', 'Option B', 'Option C', 'Option D'].map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleOptionSelect(option)}
                      className={`p-3 border rounded-lg text-left transition-all ${
                        selectedAnswer === option 
                          ? 'border-purple-600 bg-purple-50 shadow-md' 
                          : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                      }`}
                    >
                      <span className="font-medium">{String.fromCharCode(65 + index)}.</span> {option}
                    </button>
                  ))}
                </div>
              </div>

              {showAnswer && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="font-semibold text-green-800">✅ Correct Answer:</p>
                  <p className="text-green-700 mt-1">
                    The correct answer is Option A
                  </p>
                  <div className="mt-2 text-sm text-gray-600">
                    <p><strong>Reference:</strong> Surah {question.surah_name_ar} ({question.sura_number}:{question.aya_number})</p>
                    <p><strong>Page:</strong> {pageNumber}</p>
                  </div>
                </div>
              )} */}

              <div className="flex flex-wrap justify-between items-center gap-2">
                <div className="flex flex-wrap gap-2">
                  {showPrev && onPrev && (
                    <button
                      onClick={onPrev}
                      className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                    >
                      ← Previous
                    </button>
                  )}
                  <button
                    onClick={handleShowAnswer}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    disabled={showAnswer}
                  >
                    {showAnswer ? '✅ Answer Revealed' : 'Show Answer'}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={onDone}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Grid
                  </button>
                  <button
                    onClick={onNext}
                    className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-700 text-white rounded-lg hover:scale-105 transition-transform"
                  >
                    {questionNumber === 2 ? '✅ Done' : 'Next →'}
                  </button>
                </div>
              </div>
            </div>
          ) : viewMode === 'mushaf' ? (
            // Mushaf View (Page Images) - Full Width, No Padding
            <div className="w-full">
              <div className="bg-purple-50 px-4 py-2">
                <p className="text-sm text-gray-600">
                  🖼️ Showing Mushaf Page {pageNumber} - Question {questionNumber}: Verse {question.aya_number}
                </p>
              </div>
              
              <div className="w-full">
                <MushafView questionId={String(verseId)} />
              </div>
              
              <div className="flex flex-wrap justify-between items-center px-4 py-3 bg-gray-50 border-t gap-2">
                <div className="flex flex-wrap gap-2">
                  {showPrev && onPrev && (
                    <button
                      onClick={onPrev}
                      className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                    >
                      ← Previous
                    </button>
                  )}
                  <button
                    onClick={switchToQuestion}
                    className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                  >
                    📝 Show Question
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={onDone}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Grid
                  </button>
                  <button
                    onClick={onNext}
                    className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-700 text-white rounded-lg hover:scale-105 transition-transform"
                  >
                    {questionNumber === 2 ? '✅ Done' : 'Next →'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            // Text View - Full Width, No Padding
            <div className="w-full">
              <div className="bg-purple-50 px-4 py-2">
                <p className="text-sm text-gray-600">
                  📄 Showing Text View - Question {questionNumber}: Verse {question.aya_number}
                </p>
              </div>
              
              <div className="w-full">
                <TextView questionId={String(verseId)} />
              </div>
              
              <div className="flex flex-wrap justify-between items-center px-4 py-3 bg-gray-50 border-t gap-2">
                <div className="flex flex-wrap gap-2">
                  {showPrev && onPrev && (
                    <button
                      onClick={onPrev}
                      className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                    >
                      ← Previous
                    </button>
                  )}
                  <button
                    onClick={switchToQuestion}
                    className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                  >
                    📝 Show Question
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={onDone}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Grid
                  </button>
                  <button
                    onClick={onNext}
                    className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-700 text-white rounded-lg hover:scale-105 transition-transform"
                  >
                    {questionNumber === 2 ? '✅ Done' : 'Next →'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};